import type { OnboardingStep } from "@/api/onboarding";
import { DESKTOP_ONBOARDING_CONTENT_VERSION } from "@/desktop/onboarding/onboarding-content-version";
import { isTauri } from "@/lib/is-tauri";
import { getOnboardingLocalStateUser } from "@/lib/onboarding-local-state";

export type OnboardingCompletion = "completed" | "skipped";

function intentKey(): string | null {
  const userId = getOnboardingLocalStateUser();
  return isTauri() && userId
    ? `denker.onboarding.completion_intent.desktop.user.${userId}`
    : null;
}

export function clearOnboardingCompletionIntent(): void {
  const key = intentKey();
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // In-session retry remains available when storage is unavailable.
  }
}

export function saveOnboardingCompletionIntent(
  step: OnboardingCompletion,
  previousStep: OnboardingStep | null,
  persistedStep: OnboardingStep | null,
): void {
  const key = intentKey();
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        step,
        previousStep,
        persistedStep,
        contentVersion: DESKTOP_ONBOARDING_CONTENT_VERSION,
      }),
    );
  } catch {
    // In-session retry remains available when storage is unavailable.
  }
}

/** Restore only the exact unfinished guide; a restart or newer content wins. */
export function restoreOnboardingCompletionIntent(
  backendStep: string | null,
): OnboardingCompletion | null {
  const key = intentKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === "object") {
      const intent = value as Record<string, unknown>;
      if (
        (intent.step === "completed" || intent.step === "skipped") &&
        backendStep !== "completed" &&
        backendStep !== "skipped" &&
        (intent.previousStep === backendStep ||
          intent.persistedStep === backendStep) &&
        intent.contentVersion === DESKTOP_ONBOARDING_CONTENT_VERSION
      )
        return intent.step;
    }
  } catch {
    // Invalid or unavailable local state must never complete onboarding.
  }
  clearOnboardingCompletionIntent();
  return null;
}
